import { useCallback, useState } from 'react';
import { Header } from '../components/Header';
import { EarningsAhead, type EarningsSort } from '../components/EarningsAhead';
import { FinancialsSection } from '../components/Financials';
import { SymbolSearch } from '../components/SymbolSearch';
import { TabBar } from '../components/TabBar';
import * as api from '../api/client';
import type { SearchHit } from '../api/client';
import { newId } from '../lib/id';
import { todayIso } from '../lib/format';
import { useStore } from '../state/store';
import type { Watch } from '../types';
import type { Nav } from '../App';

/* What each company is about to report, and what it has reported so far.
 *
 * These numbers were reachable only from inside a position, which made them a
 * detail of owning something. They are not: the quarter a company files is the
 * same fact whether it is in the book or not, and reading it is most of how you
 * decide to own it in the first place. So results get their own tab — and the
 * search reaches any company at all, held or not, followed or not. */
export function Earnings({ nav, initialSymbol }: { nav: Nav; initialSymbol?: string }) {
  const { book, state, putWatch, cacheQuotes } = useStore();
  const [symbol, setSymbol] = useState<string | null>(initialSymbol ?? null);
  const [sort, setSort] = useState<EarningsSort>('soonest');
  const [found, setFound] = useState<SearchHit | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const openPosition = (ticker: string) => nav.go({ name: 'position', ticker });

  /* Owned first, then watched, then whatever was last looked up. All three are
     companies that file, and the picker treats them alike — the mark on the chip
     says which is which. */
  const followed = [
    ...book.positions.map((p) => ({
      key: p.ticker, label: p.ticker, symbol: p.yahooSymbol, name: p.name, kind: 'owned' as const,
    })),
    ...state.data.watchlist.map((w) => ({
      key: w.id, label: w.ticker, symbol: w.yahooSymbol, name: w.name, kind: 'watched' as const,
    })),
  ];
  const known = new Set(followed.map((f) => f.symbol));
  const picker = found && !known.has(found.symbol)
    ? [...followed, {
        key: 'found', label: found.symbol, symbol: found.symbol,
        name: found.name, kind: 'found' as const,
      }]
    : followed;

  const chosen = symbol ?? picker[0]?.symbol ?? null;
  const chosenName = picker.find((f) => f.symbol === chosen)?.name;

  const pick = useCallback((hit: SearchHit) => {
    setFound(hit);
    setSymbol(hit.symbol);
    setNote(null);
  }, []);

  const follow = async () => {
    if (!found) return;
    const entry: Watch = {
      id: newId('watch'),
      ticker: found.symbol,
      yahooSymbol: found.symbol,
      name: found.name,
      addedAt: todayIso(),
    };
    await putWatch(entry);
    /* Fetch the mark now and keep it. Waiting for the next scheduled refresh
       means, with the market shut, waiting until tomorrow — and a radar card
       reading "—" about a company you just looked up is a poor first
       impression of the feature. */
    try {
      const { rows } = await api.fetchQuotes([found.symbol]);
      await cacheQuotes(rows);
    } catch { /* the card shows no price until the next refresh, and says so */ }
    setNote(`${found.symbol} is on the radar now.`);
  };

  return (
    <div className="screen" data-tabbed="true">
      <Header onOpenSettings={() => nav.go({ name: 'settings' })} />

      <section>
        <div className="section-head">
          <h2>Earnings ahead</h2>
        </div>
        <div className="seg" style={{ marginBottom: 4 }}>
          {([['soonest', 'Soonest'], ['biggest', 'Biggest first']] as const).map(([k, label]) => (
            <button key={k} aria-pressed={sort === k} onClick={() => setSort(k)}>{label}</button>
          ))}
        </div>
        <EarningsAhead
          positions={book.positions}
          watchlist={state.data.watchlist}
          earnings={state.earnings}
          sort={sort}
          onOpen={openPosition}
          onOpenWatch={setSymbol}
        />
      </section>

      {/* How the positions stand moved to the results screen, behind the value
          chart. What a company reported and what your position did are two
          subjects, and this screen was carrying both. */}

      <section>
        <div className="section-head">
          <h2>Results</h2>
          <span className="meta">eight quarters</span>
        </div>

        <SymbolSearch
          label="Look up any company"
          placeholder="NVDA, Hesai, 2498.HK"
          onPick={pick}
        />

        {found && !known.has(found.symbol) ? (
          <div className="kv" style={{ marginBottom: 10 }}>
            <span className="k">{found.name} · {found.exchange}</span>
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                    onClick={() => void follow()}>
              Follow it
            </button>
          </div>
        ) : null}

        {note ? (
          <div className="dim" style={{ fontSize: 11.5, marginBottom: 10 }}>{note}</div>
        ) : null}

        {picker.length === 0 ? (
          <div className="empty">
            Nothing to report on yet. Look a company up above, add a position, or follow something
            on the radar.
          </div>
        ) : (
          <>
            <div className="hscroll" role="group" aria-label="Pick a company">
              {picker.map((f) => (
                <button
                  key={f.key}
                  className="btn btn-sm"
                  aria-pressed={f.symbol === chosen}
                  onClick={() => setSymbol(f.symbol)}
                >
                  {f.label}
                  {f.kind === 'owned' ? '' : f.kind === 'watched' ? ' ·' : ' ⌕'}
                </button>
              ))}
            </div>

            {chosen ? <FinancialsSection symbol={chosen} name={chosenName} /> : null}
          </>
        )}
      </section>

      <TabBar current="earnings" go={nav.go} />
    </div>
  );
}
